import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Users, MapPin, Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/constants";

const PlanCard = ({ p, onDelete, i }) => (
  <div data-testid={`plan-card-${i}`} className="rounded-2xl border border-stone-200 bg-white p-5 hover:shadow-md transition-shadow flex flex-col gap-3 rise" style={{ animationDelay: `${i * 50}ms` }}>
    <div className="flex items-center justify-between gap-2">
      <span className="font-mono text-[11px] uppercase tracking-wider text-stone-500">{p.status}</span>
      {p.role === "owner" ? (
        <span className="text-xs text-stone-500 flex items-center gap-1">{p.shared_with_users?.length ? <><Users className="w-3.5 h-3.5" /> {p.shared_with_users.length} can see</> : <><Lock className="w-3.5 h-3.5" /> Private</>}</span>
      ) : <span className="text-xs text-[#163A2B] font-semibold">From {p.owner?.name}</span>}
    </div>
    <Link to={`/plans/${p.id}`} data-testid={`plan-title-link-${i}`} className="text-xl font-bold leading-tight hover:text-[#23603F] transition-colors">{p.title}</Link>
    <p className="text-sm text-stone-600 line-clamp-2">{p.stops.map((s) => s.name).join(" → ")}</p>
    <div className="flex items-center gap-3 text-xs text-stone-500 font-mono">
      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.stops.length} stops</span>
      <span>{money(p.totals?.cost_min, p.totals?.cost_max)} est.</span>
      <span>{p.inputs?.start_time} start</span>
    </div>
    <div className="flex gap-2 mt-auto pt-2">
      <Link to={`/plans/${p.id}`} data-testid={`plan-open-${i}`} className="flex-1 text-center bg-[#163A2B] hover:bg-[#0F2A1F] text-white text-sm font-semibold py-2 rounded-xl transition-colors">Open</Link>
      {onDelete && <button data-testid={`plan-delete-${i}`} onClick={() => onDelete(p)} className="px-3 rounded-xl border border-stone-300 hover:bg-red-50 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>}
    </div>
  </div>
);

export default function MyPlans() {
  const [mine, setMine] = useState(null);
  const [shared, setShared] = useState([]);
  useEffect(() => {
    api.get("/plans").then((r) => setMine(r.data));
    api.get("/plans/shared").then((r) => setShared(r.data));
  }, []);
  const del = async (p) => {
    try { await api.delete(`/plans/${p.id}`); setMine(mine.filter((x) => x.id !== p.id)); toast.success("Plan deleted"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-6xl mx-auto px-5 py-10 lg:py-14 space-y-12" data-testid="my-plans-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#23603F]">Private by default</p>
        <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight">My Plans</h1>
      </div>
      <section>
        {mine?.length === 0 && (
          <div className="rounded-2xl border border-dashed border-stone-300 p-10 text-center" data-testid="my-plans-empty">
            <p className="text-stone-600">No saved plans yet.</p>
            <Link to="/" className="mt-3 inline-block font-semibold text-[#23603F]">Plan your first outing →</Link>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{mine?.map((p, i) => <PlanCard key={p.id} p={p} i={i} onDelete={del} />)}</div>
      </section>
      <section>
        <h2 className="text-xl sm:text-2xl font-bold mb-4">Shared with me</h2>
        {shared.length === 0 ? <p className="text-sm text-stone-500" data-testid="shared-plans-empty">When a friend shares a plan with you, it shows up here.</p>
          : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shared-plans-list">{shared.map((p, i) => <PlanCard key={p.id} p={p} i={100 + i} />)}</div>}
      </section>
    </div>
  );
}
